import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { ListChecks, Search } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const PAGE_SIZE = 10;

export default function ChecklistStatusPage() {
  const navigate = useNavigate();
  const [outlets, setOutlets] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get('/outlets').then((r) => setOutlets(r.data)).catch(() => {});
    api.get('/staff').then((r) => setAllStaff(r.data)).catch(() => {});
  }, []);

  const q = search.trim().toLowerCase();
  const visibleStaff = allStaff.filter((s) => {
    if (s.isActive === false) return false;
    if (selectedOutlet && s.outlet?._id !== selectedOutlet) return false;
    if (!q) return true;
    return [s.name, s.staffId, s.outlet?.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  const totalPages = Math.ceil(visibleStaff.length / PAGE_SIZE);
  const pageStaff = visibleStaff.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [search, selectedOutlet]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Assign Checklist</h2>
          <p className="text-muted">Select a staff member to assign a checklist and track status</p>
        </div>
        <div className="header-actions">
          <select className="form-select" value={selectedOutlet} onChange={(e) => setSelectedOutlet(e.target.value)}>
            <option value="">All Outlets</option>
            {outlets.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
          </select>
          <div className="search-bar">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff by name, ID, outlet..."
            />
          </div>
        </div>
      </div>

      <div className="table-container table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Staff ID</th>
              <th>Staff Name</th>
              <th>Outlet</th>
              <th>Checklist</th>
            </tr>
          </thead>
          <tbody>
            {pageStaff.map((s) => (
              <tr key={s._id}>
                <td><span className="badge outlet-id-badge">{s.staffId}</span></td>
                <td>
                  <div className="staff-name-cell">
                    {s.photo && <img src={`${API_BASE}/uploads/${s.photo}`} alt="" className="staff-avatar" />}
                    <strong>{s.name}</strong>
                  </div>
                </td>
                <td>{s.outlet?.name || '—'}</td>
                <td>
                  <button className="btn btn-sm btn-outline" onClick={() => navigate(`/checklist-status/${s._id}`)}>
                    <ListChecks size={13} /> Assign
                  </button>
                </td>
              </tr>
            ))}
            {visibleStaff.length === 0 && (
              <tr><td colSpan={4} className="empty-state">
                {allStaff.length === 0 ? 'No staff yet.' : 'No staff match your filters.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {visibleStaff.length > PAGE_SIZE && (
        <div className="pagination">
          <span className="pagination-info">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, visibleStaff.length)}–{Math.min(page * PAGE_SIZE, visibleStaff.length)} of {visibleStaff.length}
          </span>
          <div className="pagination-controls">
            <button className="btn btn-sm btn-outline" onClick={() => setPage(1)} disabled={page === 1}>«</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
              .map((p, idx) => p === '...'
                ? <span key={`e${idx}`} className="pagination-ellipsis">…</span>
                : <button key={p} className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-outline'}`} onClick={() => setPage(p)}>{p}</button>)}
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>Next ›</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
          </div>
        </div>
      )}
    </div>
  );
}
